import dayjs from 'dayjs';
import { useCallback, useState } from 'react';

import {
  useLazyGetFilterDataQuery,
  useLazyGetSearchResultQuery,
  useSearchBillsMutation,
} from '@app/api/Bills';
import type {
  ModelBillFilterData,
  ModelBillSearchFilter,
  ModelBillSearchResult,
} from '@app/api/Bills';
import { useUserPrint } from '@app/hooks';
import {
  DEFAULT_SEARCH_BILLS_DATA,
  getColumns,
} from '@app/pages/PaymentDocuments/components/PaymentDocumentsRegistry/data';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';

import type { BaseOptionType } from 'antd/es/select';
import type { Dayjs } from 'dayjs';
import type { NoUndefinedRangeValueType } from 'rc-picker/lib/PickerInput/RangePicker';

const rangePickerDefaultPeriod: NoUndefinedRangeValueType<Dayjs> = [
  dayjs().subtract(7, 'day'),
  dayjs(),
];

export const usePaymentDocumentsRegistry = () => {
  const [organizationIds, setOrganizationIds] = useState<
    BaseOptionType[] | null
  >(null);
  const [documentTypes, setDocumentTypes] = useState<BaseOptionType[] | null>(
    null
  );
  const [statuses, setStatuses] = useState<BaseOptionType[] | null>(null);
  const [period, setPeriod] =
    useState<NoUndefinedRangeValueType<dayjs.Dayjs> | null>(
      rangePickerDefaultPeriod
    );
  const [filters, setFilters] = useState<ModelBillFilterData | null>(null);
  const [searchBillsResult, setSearchBillsResult] =
    useState<ModelBillSearchResult | null>();
  const [pageNumber, setPageNumber] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const { showNotification } = useNotification();
  const { activeBranchId: ActiveBranchId } = useUserPrint();

  const [getFilterData, { isLoading: isFiltersLoading }] =
    useLazyGetFilterDataQuery();
  const [getSearchResult, { isLoading: isGetSearchResultLoading }] =
    useLazyGetSearchResultQuery();
  const [searchBills, { isLoading: isSearchBillsLoading }] =
    useSearchBillsMutation();

  const handleSearchFilters = useCallback(async () => {
    try {
      const result = await getFilterData({ ActiveBranchId }).unwrap();
      setFilters(result);
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  }, [ActiveBranchId, getFilterData, showNotification]);

  const handleSearchData = useCallback(
    async (data: ModelBillSearchFilter) => {
      try {
        const result = await searchBills({
          data,
          ActiveBranchId,
        }).unwrap();

        setSearchBillsResult(result);
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    },
    [searchBills, ActiveBranchId, showNotification]
  );

  const onReset = () => {
    handleSearchData(DEFAULT_SEARCH_BILLS_DATA);
    setDefaultFilters();
  };

  const onSearch = () => {
    const mappedPeriod =
      period && period[0] && period[1]
        ? {
            from: period[0].format('YYYY-MM-DD'),
            to: period[1].format('YYYY-MM-DD'),
          }
        : null;

    const requestData = {
      DateFrom: mappedPeriod?.from || '',
      DateTo: mappedPeriod?.to || '',
      OrganizationIds: organizationIds?.map((id) => Number(id.value)) || null,
      DocumentTypes: documentTypes?.map((type) => String(type.value)) || null,
      Statuses: statuses?.map((status) => String(status.value)) || null,
      RecordsPerPage: 20,
    };
    handleSearchData(requestData);
  };

  const setDefaultFilters = () => {
    setOrganizationIds(null);
    setDocumentTypes(null);
    setStatuses(null);
    setPeriod(rangePickerDefaultPeriod);
  };

  const handlePaginationChange = async (page: number, rowsPerPage: number) => {
    try {
      if (!searchBillsResult?.Id) return;

      const result = await getSearchResult({
        searchId: searchBillsResult.Id,
        page,
        rowsPerPage,
        ActiveBranchId,
      }).unwrap();

      setSearchBillsResult(result);
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  };

  const handleTableChange = (page: number, pageSize: number) => {
    setPageNumber(page);
    setRowsPerPage(pageSize);

    handlePaginationChange(page, pageSize);
  };

  const columns = getColumns(searchBillsResult?.Bills || []);

  return {
    onReset,
    onSearch,
    organizationIds,
    setOrganizationIds,
    documentTypes,
    setDocumentTypes,
    statuses,
    setStatuses,
    period,
    setPeriod,
    handleSearchFilters,
    filters,
    isFiltersLoading,
    handleSearchData,
    isSearchBillsLoading,
    searchBillsResult,
    pageNumber,
    rowsPerPage,
    handleTableChange,
    columns,
    isGetSearchResultLoading,
  };
};
